import React, { FC, Fragment, MouseEvent } from 'react';
import { formatDate, formatPatientName, NO_RESPONSIBLE_USER_PLACEHOLDER } from 'utils/format';
import { Box, Button, TableCell, TableRow, Typography } from '@mui/material';
import { Order, OrderStatus } from 'models/order';
import { useTranslation } from 'react-i18next';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import OrderCheckBox from 'components/shared/order-check-box/order-check-box.component';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { pausedLabel } from 'utils/paused-label';
import RestorationTypes, { RESTORATION_TYPES_CELL_SX } from 'components/orders/shared/restoration-types.component';
import { ORDER_LIST_COLUMN_DEFINITIONS } from 'components/orders/lab-order-table/column-registry';

// DD-3547: the table has no fixed layout, so cap the column at the order list's width to truncate.
const CONSTRUCTIONS_COLUMN_WIDTH = ORDER_LIST_COLUMN_DEFINITIONS.constructions.defaultWidth;

const captionStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

type DeliveryScheduleTableItemProps = {
    order: Order;
    batchActionSelected?: boolean;
    checked?: boolean;
    isAtLimit?: boolean;
    onHandleCheckedOrders?: (order: Order, checked: boolean) => void;
    onSentByClinicDialog?: (order: Order) => void;
    onRemoveFromScheduleDialog?: (order: Order) => void;
    onChangeInternalDeliveryDate?: (order: Order) => void;
    onChangeExternalDeliveryDate?: (order: Order) => void;
    isExternal: boolean;
    // DD-3800: the Production › Overview click-through list reuses these rows without the
    // status action buttons, and with an extra "Came in via" cell when a label is given.
    readOnly?: boolean;
    cameInViaLabel?: string;
};

const DeliveryScheduleTableItem: FC<DeliveryScheduleTableItemProps> = ({
    order,
    onSentByClinicDialog,
    onRemoveFromScheduleDialog,
    onChangeInternalDeliveryDate,
    onChangeExternalDeliveryDate,
    isExternal,
    batchActionSelected,
    checked,
    isAtLimit,
    onHandleCheckedOrders,
    readOnly,
    cameInViaLabel,
}: DeliveryScheduleTableItemProps) => {
    const { t: translate } = useTranslation(['delivery-schedule', 'workflow', 'orders']);
    const currentOrg = currentOrgVar();
    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );

    const currentStep = order.workFlow?.currentStep;
    const startedByName = currentStep?.name ? currentStep.user?.fullName : undefined;
    const departmentName = currentStep?.departmentName || undefined;
    const isCompleted = order.status === OrderStatus.SENT_BY_LAB;

    const getStepLabel = (): string => {
        if (currentStep?.name === 'NO_STEP') return translate('workflow:NO_STEP');
        if (currentStep?.name === 'ALL_STEPS_DONE') return translate('workflow:ALL_STEPS_DONE');
        return currentStep?.name || '-';
    };

    const navigateToOrderDetails = () => {
        window.open(`/order-details/${order._id}/main-view`, '_blank');
    };

    const handleSentToClinic = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (onSentByClinicDialog) {
            onSentByClinicDialog(order);
        }
    };

    const handleRemoveFromSchedule = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (onRemoveFromScheduleDialog) {
            onRemoveFromScheduleDialog(order);
        }
    };

    const handleChangeInternalDeliveryDate = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (onChangeInternalDeliveryDate) {
            onChangeInternalDeliveryDate(order);
        }
    };

    const handleChangeExternalDeliveryDate = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (onChangeExternalDeliveryDate) {
            onChangeExternalDeliveryDate(order);
        }
    };

    return (
        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => navigateToOrderDetails()}>
            {batchActionSelected ? (
                <TableCell padding="checkbox">
                    <OrderCheckBox
                        order={order}
                        checked={checked}
                        isAtLimit={isAtLimit}
                        onHandleCheckedOrders={onHandleCheckedOrders}
                    />
                </TableCell>
            ) : undefined}
            <TableCell>
                <Box display="flex" flexDirection="column">
                    <span>{order.internalOrderId ? `${order.orderId} / ${order.internalOrderId}` : order.orderId}</span>
                    {isCompleted ? (
                        <Typography
                            variant="caption"
                            component="div"
                            sx={{
                                ...captionStyle,
                                color: 'success.main',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <CheckCircleIcon fontSize="inherit" />
                            {translate('delivery-schedule:COMPLETED')}
                        </Typography>
                    ) : undefined}
                    {currentStep?.isPaused ? (
                        <Typography variant="caption" sx={{ ...captionStyle, color: 'warning.main' }}>
                            {pausedLabel(currentStep?.pauseReason, translate)}
                        </Typography>
                    ) : undefined}
                </Box>
            </TableCell>
            <TableCell>{formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}</TableCell>
            <TableCell>{order.deliveryDetails.destination?.name}</TableCell>
            <TableCell>{order.dentist?.fullName ?? NO_RESPONSIBLE_USER_PLACEHOLDER}</TableCell>
            <TableCell>{formatDate(new Date(order.deliveryDetails.date), 'date')}</TableCell>
            {isExternal && (
                <TableCell>
                    {order.deliveryDetails.externalDeliveryDate
                        ? formatDate(new Date(order.deliveryDetails.externalDeliveryDate), 'date')
                        : '-'}
                </TableCell>
            )}
            <TableCell>
                <Box display="flex" flexDirection="column">
                    <span>{getStepLabel()}</span>
                    {departmentName ? (
                        <Typography variant="caption" component="div" sx={{ ...captionStyle, color: 'text.secondary' }}>
                            {departmentName}
                        </Typography>
                    ) : undefined}
                    {startedByName ? (
                        <Typography variant="caption" component="div" sx={{ ...captionStyle, color: 'text.secondary' }}>
                            {startedByName}
                        </Typography>
                    ) : undefined}
                </Box>
            </TableCell>
            <TableCell sx={RESTORATION_TYPES_CELL_SX}>
                <Box maxWidth={CONSTRUCTIONS_COLUMN_WIDTH}>
                    <RestorationTypes sampleType={order.deliveryDetails.sampleType} types={order.restorationTypes} />
                </Box>
            </TableCell>
            {cameInViaLabel !== undefined ? <TableCell>{cameInViaLabel}</TableCell> : undefined}
            {readOnly ? undefined : (
                <Fragment>
                    <TableCell sx={{ maxWidth: '180px' }} align="right"></TableCell>
                    <TableCell sx={{ width: '300px' }} align="right">
                        <Box display="flex" alignItems="center" justifyContent="end">
                            {(!isExternal || !onRemoveFromScheduleDialog) &&
                            order.status === OrderStatus.ACCEPTED_BY_LAB ? (
                                <Button size="small" fullWidth onClick={(event) => handleSentToClinic(event)}>
                                    {translate('delivery-schedule:SEND_TO_CLINIC')}
                                </Button>
                            ) : undefined}
                            {isExternal && onRemoveFromScheduleDialog ? (
                                <Button size="small" fullWidth onClick={(event) => handleRemoveFromSchedule(event)}>
                                    {translate('delivery-schedule:REMOVE_EXTERNAL_DEADLINE')}
                                </Button>
                            ) : undefined}
                        </Box>
                        <Box>
                            {!isExternal &&
                            (order.status === OrderStatus.SENT_BY_CUSTOMER ||
                                order.status === OrderStatus.ACCEPTED_BY_LAB) ? (
                                <Button
                                    size="small"
                                    fullWidth
                                    onClick={(event) => handleChangeInternalDeliveryDate(event)}
                                >
                                    {translate('delivery-schedule:CHANGE_DEADLINE')}
                                </Button>
                            ) : undefined}
                            {isExternalLabUsersEnabled &&
                            isExternal &&
                            (order.status === OrderStatus.SENT_BY_CUSTOMER ||
                                order.status === OrderStatus.ACCEPTED_BY_LAB) ? (
                                <Button
                                    size="small"
                                    fullWidth
                                    onClick={(event) => handleChangeExternalDeliveryDate(event)}
                                >
                                    {translate('delivery-schedule:CHANGE_EXTERNAL_DEADLINE')}
                                </Button>
                            ) : undefined}
                        </Box>
                    </TableCell>
                </Fragment>
            )}
        </TableRow>
    );
};

export default DeliveryScheduleTableItem;
